// 3rd Party Imports
import { getSession } from 'next-auth/react'; 

// Function Imports
import { addAuthorizationHeader } from '@/lib/functions/addAuthorizationHeader';
import { hasErrors } from "@/lib/functions/hasErrors";
import { getOrderToken } from '@/lib/functions/getOrderToken';
import { logMessage } from '@/lib/functions/logMessage';
import { publishCartRefresh } from '@/lib/broadcast/cartSync';

// Type Imports
import { OrderState as OrderStateType } from '@/lib/types/orderState';

// Context Imports
import { OrderActions } from '@/lib/context/orderActions';

interface UpdateItemQuantityInCartResponse {
  success: boolean;
  message: string;
  errorType?: string;
}

/**
 * Update the quantity of an item in the order
 *
 * @param {string} orderItemId - The ID of the order item to update
 * @param {OrderStateType} orderState - The current state of the order.
 * @param {React.Dispatch<CartActions>} orderDispatch - The dispatch function for order actions.
 * @returns {Promise<RemoveFromCartResponse>} - The response indicating the success or failure of the operation.
 */
export async function UpdateItemQuantityInCart(
  orderItemId: number,
  quantity: number,
  orderState: OrderStateType,
  orderDispatch: React.Dispatch<OrderActions>
): Promise<UpdateItemQuantityInCartResponse> {

  // Fetch the Cart Token
  const tokenResp = await getOrderToken(orderState, orderDispatch);
  const token = tokenResp.token;

  // Stop if there is no orderToken and we can't get a new one
  if (!token) {
    return { success: false, message: 'Failed to update item quantity in cart: No cart token available' };
  }

  // Get the session to check if the user is logged in
  const session = await getSession();

  // Prepare headers for the request
  const headers = addAuthorizationHeader(
    { 'Content-Type': 'application/ld+json' },
    orderState.tokenType == 'customer' ? session?.token : null,
  ); 

  try {
    // Call the API to update the quantity
    const updateItemQuantityAPIResponse = await fetch(
      `${process.env.NEXT_PUBLIC_SITE_URL}/api/orders/${token}/items/${orderItemId}/`,
      {
        method: 'PATCH',
        headers,
        body: JSON.stringify(quantity),
      }
    );

    // Get the response JSON
    const orderObject = await updateItemQuantityAPIResponse.json();

    // Check for a valid response JSON object
    if (hasErrors(orderObject)) {
      const { error, errorType } = orderObject;
      const errorMessage = error ?? '';

      if (errorType === 'out-of-stock') {
        return { success: false, message: 'This item does not have enough stock available.', errorType };
      }

      if (errorType === 'order-total-value-limit-exceeded') {
        return { success: false, message: errorMessage, errorType };
      }

      logMessage('Error in PATCH /api/orders: ' + JSON.stringify(orderObject), 'error');
      return { success: false, message: 'Failed to update item quantity in cart: API returned errors', errorType };
    }

    publishCartRefresh(token);

    // Return success
    return { success: true, message: 'Item updated in the order successfully' };

  } catch (error: unknown) {
    // Catch unknown errors
    if (error instanceof Error) {
      logMessage('Exception in PATCH /api/orders/{tokenValue}/items/{orderItemId}: ' + error.message, 'error');
    } else {
      logMessage('Unknown error in PATCH /api/orders/{tokenValue}/items/{orderItemId}', 'error');
    }
    return { success: false, message: 'Failed to update item quantity in order: Internal server error' };
  }
}
